import { motion, useReducedMotion } from "framer-motion";
import { CalendarClock, Globe, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FounderMedallion } from "./FounderCard";

/**
 * Card for an empanelled therapist on the Pro-Bono page.
 * If `bookingUrl` is set the button links to Propract; otherwise it shows a
 * gentle "Bookings opening soon" disabled state.
 */
export default function TherapistCard({ therapist: t, index = 0 }) {
  const reduced = useReducedMotion();
  const hasBooking = Boolean(t.bookingUrl);

  return (
    <motion.article
      initial={reduced ? false : { opacity: 0, y: 12 }}
      whileInView={reduced ? undefined : { opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`therapist-card-${t.slug}`}
      className="flex flex-col bg-paper rounded-3xl border border-[rgba(74,63,57,0.10)] shadow-[0_10px_30px_rgba(31,26,23,0.05)] hover:shadow-[0_18px_50px_rgba(31,26,23,0.10)] transition-shadow overflow-hidden"
    >
      <div className="p-6 sm:p-7 flex flex-col flex-1">
        <div className="flex items-start gap-4">
          <FounderMedallion initials={t.initials} slug={t.slug} photoUrl={t.photoUrl} />
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-xl sm:text-2xl text-ink leading-tight">
              {t.name}
            </h3>
            <p className="mt-1 text-sm text-ink-muted leading-snug">{t.title}</p>
            <p className="mt-1 text-[12.5px] text-terracotta font-medium leading-snug">
              {t.credentials}
            </p>
          </div>
        </div>

        <p className="mt-5 text-ink leading-[1.7] text-[15px]">{t.focus}</p>

        <div className="mt-5 space-y-3 text-sm">
          {t.modalities && t.modalities.length > 0 && (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-ink-muted font-semibold mb-2">
                Approach
              </div>
              <ul className="flex flex-wrap gap-2">
                {t.modalities.map((m) => (
                  <li
                    key={m}
                    className="text-[12.5px] px-2.5 py-1 rounded-full bg-[rgba(126,155,134,0.18)] text-moss border border-[rgba(126,155,134,0.28)]"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {t.languages && t.languages.length > 0 && (
            <div className="flex items-center gap-2 text-ink-muted text-[13px]">
              <Globe className="w-4 h-4 text-moss" />
              <span>{t.languages.join(" · ")}</span>
            </div>
          )}

          {t.availability && (
            <div className="flex items-center gap-2 text-ink-muted text-[13px]">
              <CalendarClock className="w-4 h-4 text-terracotta" />
              <span>{t.availability}</span>
            </div>
          )}
        </div>

        <div className="mt-auto pt-6">
          {hasBooking ? (
            <a href={t.bookingUrl} target="_blank" rel="noreferrer">
              <Button
                data-testid={`therapist-book-${t.slug}`}
                className="w-full bg-[#C86A4A] hover:bg-[#B85F41] text-white rounded-xl transition-colors"
              >
                Book with {t.name.split(" ")[0]}
              </Button>
            </a>
          ) : (
            <div
              data-testid={`therapist-book-soon-${t.slug}`}
              className="w-full text-center rounded-xl px-4 py-3 bg-[rgba(242,215,182,0.35)] border border-[rgba(200,106,74,0.22)] text-[#8a4128]"
            >
              <div className="flex items-center justify-center gap-2 text-sm font-semibold">
                <MessagesSquare className="w-4 h-4" />
                Bookings opening soon
              </div>
              <div className="mt-1 text-[11.5px] text-ink-muted">
                Message us on WhatsApp for a referral in the meantime
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.article>
  );
}
